import type { Difficulty, LevelDefinition } from "@/domain";

import { createFocusSocialLevel } from "./create-level";

export interface SearchObjectDefinition {
  id: string;
  label: string;
}

export interface TargetSearchRoundDefinition {
  id: string;
  prompt: string;
  promptAudio: string;
  visibleObjects: SearchObjectDefinition[];
  targetIds: string[];
  distractorIds: string[];
}

export interface TargetSearchPayload {
  rounds: TargetSearchRoundDefinition[];
}

type TargetSearchRoundInput = Omit<
  TargetSearchRoundDefinition,
  "promptAudio"
>;

function withTargetSearchAudio(
  difficulty: Difficulty,
  payload: { rounds: TargetSearchRoundInput[] }
): TargetSearchPayload {
  return {
    rounds: payload.rounds.map((round) => ({
      ...round,
      promptAudio:
        "/audio/focus-social/target-search-" +
        String(difficulty) +
        "-" +
        round.id +
        ".ogg"
    }))
  };
}

export const targetSearchLevels: LevelDefinition<TargetSearchPayload>[] = [
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 1,
      title: "蓝色水杯在哪里",
      instruction: "蓝色水杯藏在这些东西里。慢慢看，把它找出来。",
      objectives: ["在四个不同物品中找到一个明显目标", "按自己的节奏观察画面"]
    },
    withTargetSearchAudio(1, {
      rounds: [
        {
          id: "round-1",
          prompt: "蓝色水杯在哪里？把它找出来。",
          visibleObjects: [
            { id: "blue-cup", label: "蓝色水杯" },
            { id: "red-ball", label: "红色皮球" },
            { id: "yellow-kite", label: "黄色风筝" },
            { id: "green-leaf", label: "绿色叶子" }
          ],
          targetIds: ["blue-cup"],
          distractorIds: ["red-ball", "yellow-kite", "green-leaf"]
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 2,
      title: "黄色雨伞在哪里",
      instruction: "黄色雨伞也藏进来了。看看颜色和样子，把它找出来。",
      objectives: ["同时留意颜色和物品种类", "从相似物品中辨认目标"]
    },
    withTargetSearchAudio(2, {
      rounds: [
        {
          id: "round-1",
          prompt: "黄色雨伞在哪里？把它找出来。",
          visibleObjects: [
            { id: "yellow-umbrella", label: "黄色雨伞" },
            { id: "red-umbrella", label: "红色雨伞" },
            { id: "blue-umbrella", label: "蓝色雨伞" },
            { id: "yellow-hat", label: "黄色帽子" },
            { id: "yellow-rain-boot", label: "黄色雨靴" }
          ],
          targetIds: ["yellow-umbrella"],
          distractorIds: [
            "red-umbrella",
            "blue-umbrella",
            "yellow-hat",
            "yellow-rain-boot"
          ]
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 3,
      title: "星星书包",
      instruction: "两个带星星的书包躲在一起。看清图案，把它们都找出来。",
      objectives: ["根据两个特征寻找目标", "在一轮中找到多个目标"]
    },
    withTargetSearchAudio(3, {
      rounds: [
        {
          id: "round-1",
          prompt: "带星星的书包有两个，把它们都找出来。",
          visibleObjects: [
            { id: "red-star-backpack", label: "红色星星书包" },
            { id: "blue-star-backpack", label: "蓝色星星书包" },
            { id: "red-dot-backpack", label: "红色圆点书包" },
            { id: "blue-stripe-backpack", label: "蓝色条纹书包" },
            { id: "red-star-pencil-case", label: "红色星星铅笔盒" },
            { id: "blue-star-hat", label: "蓝色星星帽子" }
          ],
          targetIds: ["red-star-backpack", "blue-star-backpack"],
          distractorIds: [
            "red-dot-backpack",
            "blue-stripe-backpack",
            "red-star-pencil-case",
            "blue-star-hat"
          ]
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 4,
      title: "两轮细心找",
      instruction: "两轮寻找，点点陪着你。每一轮先看清楚，再慢慢选择。",
      objectives: ["在两轮任务中保持注意", "分辨大小、颜色和物品种类"]
    },
    withTargetSearchAudio(4, {
      rounds: [
        {
          id: "round-1",
          prompt: "小小的绿色叶子藏在哪儿？",
          visibleObjects: [
            { id: "small-green-leaf", label: "小绿色叶子" },
            { id: "large-green-leaf", label: "大绿色叶子" },
            { id: "small-yellow-leaf", label: "小黄色叶子" },
            { id: "small-red-leaf", label: "小红色叶子" },
            { id: "small-green-flower", label: "小绿色花朵" },
            { id: "small-green-kite", label: "小绿色风筝" }
          ],
          targetIds: ["small-green-leaf"],
          distractorIds: [
            "large-green-leaf",
            "small-yellow-leaf",
            "small-red-leaf",
            "small-green-flower",
            "small-green-kite"
          ]
        },
        {
          id: "round-2",
          prompt: "长长的蓝色铅笔藏在哪儿？",
          visibleObjects: [
            { id: "long-blue-pencil", label: "长蓝色铅笔" },
            { id: "short-blue-pencil", label: "短蓝色铅笔" },
            { id: "long-red-pencil", label: "长红色铅笔" },
            { id: "long-green-pencil", label: "长绿色铅笔" },
            { id: "long-blue-crayon", label: "长蓝色蜡笔" },
            { id: "long-blue-ruler", label: "长蓝色尺子" }
          ],
          targetIds: ["long-blue-pencil"],
          distractorIds: [
            "short-blue-pencil",
            "long-red-pencil",
            "long-green-pencil",
            "long-blue-crayon",
            "long-blue-ruler"
          ]
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 5,
      title: "相似物品大搜索",
      instruction: "两轮里都藏着两个目标。把两个条件一起看清楚，再把它们找出来。",
      objectives: ["在较多相似物品中寻找两个目标", "持续比较三个视觉特征"]
    },
    withTargetSearchAudio(5, {
      rounds: [
        {
          id: "round-1",
          prompt: "红色、只有两个孔的纽扣有两个，把它们找出来。",
          visibleObjects: [
            { id: "round-red-two-hole-button", label: "圆形红色两孔纽扣" },
            { id: "square-red-two-hole-button", label: "方形红色两孔纽扣" },
            { id: "round-red-four-hole-button", label: "圆形红色四孔纽扣" },
            { id: "square-red-four-hole-button", label: "方形红色四孔纽扣" },
            { id: "round-blue-two-hole-button", label: "圆形蓝色两孔纽扣" },
            { id: "square-green-two-hole-button", label: "方形绿色两孔纽扣" },
            { id: "round-yellow-four-hole-button", label: "圆形黄色四孔纽扣" },
            { id: "square-blue-four-hole-button", label: "方形蓝色四孔纽扣" }
          ],
          targetIds: [
            "round-red-two-hole-button",
            "square-red-two-hole-button"
          ],
          distractorIds: [
            "round-red-four-hole-button",
            "square-red-four-hole-button",
            "round-blue-two-hole-button",
            "square-green-two-hole-button",
            "round-yellow-four-hole-button",
            "square-blue-four-hole-button"
          ]
        },
        {
          id: "round-2",
          prompt: "有条纹、脚跟是黄色的袜子有两只，把它们找出来。",
          visibleObjects: [
            { id: "blue-stripe-yellow-heel-sock", label: "蓝条纹黄脚跟袜子" },
            { id: "green-stripe-yellow-heel-sock", label: "绿条纹黄脚跟袜子" },
            { id: "blue-dot-yellow-heel-sock", label: "蓝圆点黄脚跟袜子" },
            { id: "green-dot-yellow-heel-sock", label: "绿圆点黄脚跟袜子" },
            { id: "blue-stripe-red-heel-sock", label: "蓝条纹红脚跟袜子" },
            { id: "green-stripe-blue-heel-sock", label: "绿条纹蓝脚跟袜子" },
            { id: "yellow-star-red-heel-sock", label: "黄星星红脚跟袜子" },
            { id: "red-dot-blue-heel-sock", label: "红圆点蓝脚跟袜子" }
          ],
          targetIds: [
            "blue-stripe-yellow-heel-sock",
            "green-stripe-yellow-heel-sock"
          ],
          distractorIds: [
            "blue-dot-yellow-heel-sock",
            "green-dot-yellow-heel-sock",
            "blue-stripe-red-heel-sock",
            "green-stripe-blue-heel-sock",
            "yellow-star-red-heel-sock",
            "red-dot-blue-heel-sock"
          ]
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "target-search",
    {
      difficulty: 6,
      title: "三轮观察挑战",
      instruction: "三轮寻找等着你。这里没有倒计时，想多久都可以。",
      objectives: ["在三轮任务中灵活转换寻找规则", "综合颜色、图案和形状寻找目标"]
    },
    withTargetSearchAudio(6, {
      rounds: [
        {
          id: "round-1",
          prompt: "有圆点、把手朝右的小杯子有两个，把它们找出来。",
          visibleObjects: [
            { id: "red-dot-right-cup", label: "红色圆点右把手杯子" },
            { id: "blue-dot-right-cup", label: "蓝色圆点右把手杯子" },
            { id: "red-dot-left-cup", label: "红色圆点左把手杯子" },
            { id: "blue-dot-left-cup", label: "蓝色圆点左把手杯子" },
            { id: "red-stripe-right-cup", label: "红色条纹右把手杯子" },
            { id: "blue-star-right-cup", label: "蓝色星星右把手杯子" },
            { id: "green-dot-left-cup", label: "绿色圆点左把手杯子" },
            { id: "yellow-stripe-left-cup", label: "黄色条纹左把手杯子" }
          ],
          targetIds: ["red-dot-right-cup", "blue-dot-right-cup"],
          distractorIds: [
            "red-dot-left-cup",
            "blue-dot-left-cup",
            "red-stripe-right-cup",
            "blue-star-right-cup",
            "green-dot-left-cup",
            "yellow-stripe-left-cup"
          ]
        },
        {
          id: "round-2",
          prompt: "紫色、长着两片叶子的花朵有两朵，把它们找出来。",
          visibleObjects: [
            { id: "round-purple-two-leaf-flower", label: "圆瓣紫色两叶花" },
            { id: "point-purple-two-leaf-flower", label: "尖瓣紫色两叶花" },
            { id: "round-purple-one-leaf-flower", label: "圆瓣紫色一叶花" },
            { id: "point-purple-three-leaf-flower", label: "尖瓣紫色三叶花" },
            { id: "round-red-two-leaf-flower", label: "圆瓣红色两叶花" },
            { id: "point-yellow-two-leaf-flower", label: "尖瓣黄色两叶花" },
            { id: "round-blue-one-leaf-flower", label: "圆瓣蓝色一叶花" },
            { id: "point-green-three-leaf-flower", label: "尖瓣绿色三叶花" }
          ],
          targetIds: [
            "round-purple-two-leaf-flower",
            "point-purple-two-leaf-flower"
          ],
          distractorIds: [
            "round-purple-one-leaf-flower",
            "point-purple-three-leaf-flower",
            "round-red-two-leaf-flower",
            "point-yellow-two-leaf-flower",
            "round-blue-one-leaf-flower",
            "point-green-three-leaf-flower"
          ]
        },
        {
          id: "round-3",
          prompt: "蓝色蝴蝶结、方形盒盖的礼盒有两个，把它们找出来。",
          visibleObjects: [
            { id: "red-box-blue-bow-square-lid", label: "红盒蓝蝴蝶结方盒盖礼盒" },
            { id: "green-box-blue-bow-square-lid", label: "绿盒蓝蝴蝶结方盒盖礼盒" },
            { id: "red-box-yellow-bow-square-lid", label: "红盒黄蝴蝶结方盒盖礼盒" },
            { id: "green-box-red-bow-square-lid", label: "绿盒红蝴蝶结方盒盖礼盒" },
            { id: "red-box-blue-bow-round-lid", label: "红盒蓝蝴蝶结圆盒盖礼盒" },
            { id: "green-box-blue-bow-round-lid", label: "绿盒蓝蝴蝶结圆盒盖礼盒" },
            { id: "yellow-box-green-bow-round-lid", label: "黄盒绿蝴蝶结圆盒盖礼盒" },
            { id: "blue-box-red-bow-round-lid", label: "蓝盒红蝴蝶结圆盒盖礼盒" }
          ],
          targetIds: [
            "red-box-blue-bow-square-lid",
            "green-box-blue-bow-square-lid"
          ],
          distractorIds: [
            "red-box-yellow-bow-square-lid",
            "green-box-red-bow-square-lid",
            "red-box-blue-bow-round-lid",
            "green-box-blue-bow-round-lid",
            "yellow-box-green-bow-round-lid",
            "blue-box-red-bow-round-lid"
          ]
        }
      ]
    })
  )
];
